import { Injectable, OnDestroy } from '@angular/core';
import { Observable, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, distinctUntilChanged, exhaustMap, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { BaseHttpService } from '../api/base-http.service';
import { SynchronizationApiService } from '../api/synchronization-api.service';
import { NotificationApiService } from '../api/notification-api.service';
import { GetNotificationsForUserRequest, GetNotificationsForUserResponse } from '../../models/dtos';
import { CollaborationApiService } from '../api/collaboration-api.service';
import { CollaborationState, IsSyncNeededRequest, SynchronizationState, GetAccessRequestsRequest } from '../../models/dtos';
import { IPollingConfig } from '../../models/common/interfaces/IPollingConfig';
import { CommonDataService } from '../common-data.service';
import { UserProfileService } from './user-profile.service';
import { withSilentBackgroundRequest } from '../../core/errors/http-context-tokens';
import { CONSTANTS } from '../../Constants/Constants';
import { IAccessRequestPollingResult } from '../../models/collaboration/IAccessRequestPollingResult';
import { ICollaborationPollingSnapshot } from '../../models/collaboration/ICollaborationPollingSnapshot';

@Injectable({
  providedIn: 'root'
})
export class ApiPollingService implements OnDestroy {
  private defaultPollIntervalMs = 540000; // 9 minutes
  private syncIntervalTimeMS = 180000; // 3 minutes
  private readonly accessRequestPollIntervalMs = 60000; // 1 minute
  private readonly collaborationDefinitionPollIntervalMs = 60000; // 1 minute
  private readonly pollingConfigPath = 'assets/polling-config.json';
  private readonly destroy$ = new Subject<void>();
  private configSubscription: Subscription = Subscription.EMPTY;

  constructor(private readonly baseHttp: BaseHttpService,
    private readonly synchronizationApiService: SynchronizationApiService,
    private readonly notificationApiService: NotificationApiService,
    private readonly collaborationApiService: CollaborationApiService,
    private readonly commonDataService: CommonDataService,
    private readonly userProfileService: UserProfileService,
    private readonly translate: TranslateService
  ) {
    this.getDefaultPollInterval();
  }

  getDefaultPollInterval(): void {
    this.configSubscription = this.baseHttp.get<IPollingConfig>(this.pollingConfigPath, undefined, {
      context: withSilentBackgroundRequest()
    }).subscribe({
      next: (config) => {
        const parsedInterval = Number(config?.defaultPollIntervalMs);
        if (Number.isFinite(parsedInterval) && parsedInterval > 0) {
          this.defaultPollIntervalMs = parsedInterval;
        }

        const parsedSyncInterval = Number(config?.syncIntervalTimeMS);
        if (Number.isFinite(parsedSyncInterval) && parsedSyncInterval > 0) {
          this.syncIntervalTimeMS = parsedSyncInterval;
        }
      },
      error: () => {
        console.error('Failed to load recommended polling interval from config. Using default value of 9 minutes.'); //TO-DO: need to add proper error handling
      }
    });
  }

  /**
   * Creates a timer Observable that fires at clock-aligned boundaries.
   *
   * Instead of counting from "now", it waits until the next clean multiple of
   * the interval on the wall clock, then repeats on every interval thereafter.
   *
   * Example — interval 60 000 ms (1 min), started at 12:30:40:
   *   initial delay = 60 000 − (Date.now() % 60 000)  →  fires at 12:31:00
   *   then repeats:  12:32:00 · 12:33:00 · …
   *
   * This makes log timestamps predictable and easy to compare across runs.
   *
   * @param intervalMs  Polling interval in milliseconds (must be > 0).
   */
  private createAlignedTimer(intervalMs: number): Observable<number> {
    const msUntilNextBoundary = intervalMs - (Date.now() % intervalMs);
    return timer(msUntilNextBoundary, intervalMs).pipe(takeUntil(this.destroy$));
  }

  // Context is applied for a silent background API call (no global error handling or loading spinner).
  getNotificationsMessages(): Observable<boolean> {
    const request: GetNotificationsForUserRequest = {
      userId: this.userProfileService.userInfo?.gid ?? ''
    }

    return timer(0, this.defaultPollIntervalMs).pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.notificationApiService.getNotificationsForUser(request).pipe(
        catchError(() => of({ notifications: [] } as GetNotificationsForUserResponse))
      )),
      map((response) => {
        const notifications = response?.notifications ?? [];
        this.commonDataService.setNotifications(notifications);
        if (!notifications.length) {
          return false;
        }
        const hasUnread = notifications.some(n => !n.isRead);
        return hasUnread;
      })
    );
  }

  isSyncNeeded(): Observable<boolean> {
    return this.createAlignedTimer(this.syncIntervalTimeMS).pipe(
      switchMap(() => {
        const request: IsSyncNeededRequest = {
          userId: this.userProfileService.userInfo?.gid ?? '',
          lastSyncDate: this.commonDataService.getLastSyncDate()
        };
        return this.synchronizationApiService.isSyncNeeded(request);
      }),
      map((response) => response?.synchronizationStatus === SynchronizationState.SyncNeeded),
      distinctUntilChanged(),
      tap((isSyncNeeded) => this.commonDataService.setIsSyncNeeded(isSyncNeeded)),
      catchError(() => {
        this.commonDataService.setIsSyncNeeded(true);
        return of(false);
      })
    );
  }

  pollCollaborationDefinition(): Observable<ICollaborationPollingSnapshot | null> {
    return this.createAlignedTimer(this.collaborationDefinitionPollIntervalMs).pipe(
      exhaustMap((): Observable<ICollaborationPollingSnapshot | null> => {
        const estimateId = this.commonDataService.getEstimateId();
        const estimateSession = this.commonDataService.getEstimateSession();
        const email = this.userProfileService.userInfo?.mail ?? CONSTANTS.EMPTY_STRING;

        if (estimateId === CONSTANTS.DEFAULT_ESTIMATE_ID || email === CONSTANTS.EMPTY_STRING || !navigator.onLine) {
          return of(null);
        }

        if (!this.commonDataService.isCollaborationEstimate()) {
          return of(null);
        }

        const collaborationState = this.commonDataService.getCollaborationState();
        if (collaborationState === CollaborationState.Edit) {
          return of(null); // Never fetch a replacement snapshot while the user may have uncommitted edits.
        }

        return this.collaborationApiService.getCollaborationInfo().pipe(
          map((collaborationInfo): ICollaborationPollingSnapshot | null =>
            collaborationInfo ? { estimateId, estimateSession, collaborationInfo } : null),
          catchError(() => of(null))
        );
      })
    );
  }

  pollAccessRequests(): Observable<IAccessRequestPollingResult> {
    return this.createAlignedTimer(this.accessRequestPollIntervalMs).pipe(
      exhaustMap(() => {
        const estimateId = this.commonDataService.getEstimateId();
        const estimateSession = this.commonDataService.getEstimateSession();
        const email = this.userProfileService.userInfo?.mail ?? CONSTANTS.EMPTY_STRING;
        const empty: IAccessRequestPollingResult = { estimateId, accessRequests: [], declineAccessResponse: null };

        if (estimateId === CONSTANTS.DEFAULT_ESTIMATE_ID || email === CONSTANTS.EMPTY_STRING ||
          !navigator.onLine || !this.commonDataService.isCollaborationEstimate()) {
          return of(empty);
        }

        const collaborationState = this.commonDataService.getCollaborationState();

        if (collaborationState === CollaborationState.Edit) {
          const request: GetAccessRequestsRequest = {
            autoDeclineNotes: this.translate.instant('COLLABORATIVE_ESTIMATE_AUTO_DECLINE_MESSAGE')
          };
          return this.collaborationApiService.getAccessRequests(request, withSilentBackgroundRequest()).pipe(
            filter(() => estimateSession === this.commonDataService.getEstimateSession()
              && this.commonDataService.getCollaborationState() === CollaborationState.Edit),
            map((requests): IAccessRequestPollingResult => ({
              estimateId,
              accessRequests: estimateSession === this.commonDataService.getEstimateSession()
                && this.commonDataService.getCollaborationState() === CollaborationState.Edit ? requests ?? [] : [],
              declineAccessResponse: null
            })),
            catchError(() => of(empty).pipe(filter(() => estimateSession === this.commonDataService.getEstimateSession())))
          );
        }

        return of(empty);
      })
    );
  }


   ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.configSubscription.unsubscribe();
  }
}